import React, { useState, useEffect } from 'react';
import { Business } from '../types';
import SocialShare from './SocialShare';
import api, { reviewAPI } from '../services/api';
import ReviewComponent from './ReviewComponent';
import {
  Star,
  MapPin,
  Phone,
  Globe,
  X,
  Heart,
  Share2,
  Users,
  MessageSquare,
  ThumbsUp,
  BarChart2
} from 'lucide-react';

interface BusinessDetailsProps {
  business: Business;
  user: any
  onStartReview: () => void;
  onClose: () => void;
  onViewFullProfile?: () => void;
  onUserUpdate: () => void;
  onRefresh?: number;
  onBookmark?: (businessId: string) => void;
  isBookmarked?: boolean;
  positiveMinRating: number;
}

const BusinessDetails: React.FC<BusinessDetailsProps> = ({
  business,
  user,
  onStartReview,
  onClose,
  onViewFullProfile,
  onUserUpdate,
  onRefresh,
  onBookmark,
  isBookmarked = false,
  positiveMinRating
}) => {
  const reviewLimit = 2;

  const [showShareModal, setShowShareModal] = useState(false);
  const [positiveReviews, setPositiveReviews] = useState<any[]>([]);
  const [positiveAvgRating, setPositiveAvgRating] = useState<number>(0);
  const [positiveTotalReviews, setPositiveTotalReviews] = useState<number>(0);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [reviewsPage, setReviewsPage] = useState<number>(1);
  const [reviewsTotalPages, setReviewsTotalPages] = useState<number>(1);
  const [reviewStats, setReviewStats] = useState<{
    ratingBreakdown: { [key: number]: number };
    responseRate: number;
    totalReviews: number;
    respondedCount: number;
  } | null>(null);

  useEffect(() => {
    // Reset pagination when the business changes
    setReviewsPage(1);
  }, [business.id]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoadingReviews(true);
        const [data, stats] = await Promise.all([
          reviewAPI.getByBusiness(business.id, positiveMinRating, reviewsPage, reviewLimit),
          reviewAPI.getStats(business.id).catch(() => null),
        ]);
        setPositiveReviews(data.data);
        setPositiveAvgRating(data.averageRating);
        setPositiveTotalReviews(
          data.totalReviews ?? data.totalFilteredReviews ?? data.pagination?.total ?? 0
        );
        setReviewsTotalPages(data.pagination?.totalPages ?? 1);

        if (stats) {
          const breakdown = stats.ratingBreakdown || {};
          const totalAll = Object.values(breakdown).reduce((s: number, v: any) => s + Number(v), 0);
          const respondedCount = Math.round((stats.responseRate / 100) * totalAll);
          setReviewStats({
            ratingBreakdown: breakdown,
            responseRate: stats.responseRate || 0,
            totalReviews: totalAll,
            respondedCount,
          });
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setPositiveReviews([]);
        setReviewsTotalPages(1);
      } finally {
        setLoadingReviews(false);
      }
    };
    fetchReviews();
  }, [business.id, onRefresh, positiveMinRating, reviewLimit, reviewsPage]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${i < Math.floor(rating)
          ? 'text-yellow-400 fill-current'
          : 'text-gray-300'
          }`}
      />
    ));
  };




  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Business Details</h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Business Info */}
        <div>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {business.name}
              </h3>
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full">
                {business.category}
              </span>
            </div>
            <button
              onClick={() => onBookmark?.(business.id)}
              className={`p-2  rounded-lg transition-colors duration-200 ${isBookmarked
                ? 'bg-red-500 text-white'
                : 'bg-white bg-opacity-20 text-gray-400 hover:text-red-500 hover:bg-red-50'
                }`}>
              <Heart className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />
            </button>
          </div>

          {/* Rating */}
          <div className="flex items-center space-x-2 mb-4">
            <div className="flex items-center">
              {renderStars(positiveAvgRating || 0)}
            </div>
            <span className="text-lg font-semibold text-gray-900">
              {positiveAvgRating?.toFixed(1)}
            </span>
            <span className="text-gray-600">
              ({positiveTotalReviews} reviews)
            </span>
          </div>

          {/* Contact Info */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">{business.address}</span>
            </div>

            {business.phone && (
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <a
                  href={`tel:${business.phone}`}
                  className="text-primary-600 hover:text-primary-700 transition-colors duration-200"
                >
                  {business.phone}
                </a>
              </div>
            )}

            {business.website && (
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <a
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 transition-colors duration-200"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>

          {/* Description */}
          {business.description && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">{business.description}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={onStartReview}
            className="w-full bg-accent-600 hover:bg-accent-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <Heart className="w-5 h-5" />
            <span>Show Some Love</span>
          </button>

          {onViewFullProfile && (
            <button
              onClick={onViewFullProfile}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Globe className="w-5 h-5" />
              <span>View Full Profile</span>
            </button>
          )}

          <button
            onClick={() => setShowShareModal(true)}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <Share2 className="w-5 h-5" />
            <span>Share Business</span>
          </button>
        </div>

        {/* Review Stats Overview */}
        {reviewStats && reviewStats.totalReviews > 0 && (
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-primary-600" />
              Review Overview
            </h4>

            {/* Rating bars */}
            <div className="space-y-1.5">
              {[5, 3, 1].map((starVal) => {
                const count = reviewStats.ratingBreakdown[starVal] || 0;
                const pct = reviewStats.totalReviews > 0 ? (count / reviewStats.totalReviews) * 100 : 0;
                const barColor = starVal === 5 ? 'bg-green-500' : starVal === 3 ? 'bg-blue-400' : 'bg-orange-400';
                return (
                  <div key={starVal} className="flex items-center gap-2 text-xs">
                    <div className="flex items-center gap-0.5 w-16 flex-shrink-0">
                      {Array.from({ length: starVal }, (_, i) => (
                        <Star key={i} className="w-2.5 h-2.5 fill-current text-yellow-400" />
                      ))}
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`${barColor} h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-8 text-right text-gray-500">{count}</span>
                  </div>
                );
              })}
            </div>

            {/* Response rate */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <MessageSquare className="w-3.5 h-3.5 text-blue-500" />
                <span>Business responses</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-gray-700">
                  {reviewStats.responseRate.toFixed(0)}%
                </span>
                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-500 h-1.5 rounded-full"
                    style={{ width: `${Math.min(reviewStats.responseRate, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Good vs other summary */}
            <div className="flex items-center gap-2 text-xs text-gray-600 bg-white rounded-lg px-3 py-2">
              <ThumbsUp className="w-3.5 h-3.5 text-green-500" />
              <span>
                <span className="font-semibold text-green-600">
                  {((((reviewStats.ratingBreakdown[5] || 0) + (reviewStats.ratingBreakdown[3] || 0)) / reviewStats.totalReviews) * 100).toFixed(0)}%
                </span>{' '}
                of reviewers gave a positive rating
              </span>
            </div>
          </div>
        )}

        {/* Recent Reviews */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Recent Reviews</h4>
            <div className="flex items-center space-x-1 text-sm text-gray-600">
              <Users className="w-4 h-4" />
              <span>{positiveTotalReviews} total</span>
            </div>
          </div>

          <ReviewComponent
            reviews={positiveReviews}
            loading={loadingReviews}
            user={user}
            onHelpfulClick={async (reviewId) => {
              if (!user?.id) return;
              await api.review.addHelpfulVote(reviewId, user.id);
              onUserUpdate();
            }}
          />

          {/* Reviews Pagination */}
          {reviewsTotalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                type="button"
                disabled={loadingReviews || reviewsPage <= 1}
                onClick={() => setReviewsPage(p => Math.max(1, p - 1))}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="text-sm text-gray-600">
                Page {reviewsPage} of {reviewsTotalPages}
              </div>

              <button
                type="button"
                disabled={loadingReviews || reviewsPage >= reviewsTotalPages}
                onClick={() => setReviewsPage(p => Math.min(reviewsTotalPages, p + 1))}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Social Share Modal */}
      {showShareModal && (
        <SocialShare
          business={business}
          type="business"
          onClose={() => setShowShareModal(false)}
        />
      )}

    </div>
  );
};

export default BusinessDetails;
